import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getVendors = createAsyncThunk(
  "Vendor/get",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(`${API_URL.VENDOR.SLUG}?${queryString}`);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Lookup variant — hits the permission-free /lookup endpoint. Used by vendor
// dropdowns on screens of other modules; the Vendor management list keeps getVendors.
export const getVendorsLookup = createAsyncThunk(
  "Vendor/getLookup",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(`${API_URL.VENDOR.LOOKUP}?${queryString}`);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getActiveVendors = createAsyncThunk(
  "Vendor/getActive",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.VENDOR.ACTIVE_LOOKUP}?${queryString}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createVendor = createAsyncThunk(
  "Vendor/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL.VENDOR.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteVendor = createAsyncThunk(
  "Vendor/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.VENDOR.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const editVendor = createAsyncThunk(
  "Vendor/put",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.VENDOR.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

const vendorSlice = createSlice({
  name: "vendor",
  initialState: {
    vendor_list: [],
    active_vendor_list: [],
    current_values: null,
    active_payload: null,
    total_records: 0,
  },
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.vendor_list = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVendors.fulfilled, (state, action) => {
        // Handle different possible response structures
        if (action.payload?.data?.list) {
          state.vendor_list = action.payload.data.list;
        } else if (action.payload?.list) {
          state.vendor_list = action.payload.list;
        } else if (Array.isArray(action.payload?.data)) {
          state.vendor_list = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.vendor_list = action.payload;
        } else {
          state.vendor_list = [];
        }
        state.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getVendors.rejected, (state, action) => {
        state.vendor_list = [];
      })
      .addCase(getVendorsLookup.fulfilled, (state, action) => {
        if (action.payload?.data?.list) {
          state.vendor_list = action.payload.data.list;
        } else if (action.payload?.list) {
          state.vendor_list = action.payload.list;
        } else if (Array.isArray(action.payload?.data)) {
          state.vendor_list = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.vendor_list = action.payload;
        } else {
          state.vendor_list = [];
        }
        state.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getVendorsLookup.rejected, (state, action) => {
        state.vendor_list = [];
      })
      .addCase(getActiveVendors.fulfilled, (state, action) => {
        // Active vendors API returns data wrapped in { data: { list: [...] } }
        // Store in separate active_vendor_list to avoid conflicts with vendor_list
        if (
          action.payload?.data?.list &&
          Array.isArray(action.payload.data.list)
        ) {
          state.active_vendor_list = action.payload.data.list;
        } else if (action.payload?.list && Array.isArray(action.payload.list)) {
          state.active_vendor_list = action.payload.list;
        } else if (Array.isArray(action.payload?.data)) {
          state.active_vendor_list = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.active_vendor_list = action.payload;
        } else {
          state.active_vendor_list = [];
        }
      })
      .addCase(getActiveVendors.rejected, (state, action) => {
        state.active_vendor_list = [];
      });
  },
});

export const { setCurrentValues, setActivePayload, resetCurrentValue } =
  vendorSlice.actions;
export default vendorSlice.reducer;
